import { ComponentProps } from "react";
import { cn } from "../../lib/utils";
import { useSandboxedHTMLPreview } from "./useSandboxedHTMLPreview";
import { useTranslation } from "react-i18next";

export interface SandboxedHTMLPreviewProps extends Omit<ComponentProps<"iframe">, "srcDoc" | "sandbox" | "onLoad"> {
  /**
   * HTML to preview. It renders inside a sandboxed iframe (`sandbox="allow-same-origin"`, so no
   * scripts execute). Pass already-sanitized HTML — e.g. from `@tellescope/utils`
   * `sanitizeEmailHTMLContent` — for content-level safety in addition to the sandbox.
   */
  html: string;
}

/**
 * Renders untrusted HTML inside a sandboxed iframe so it cannot execute scripts or reach the
 * parent document, and auto-sizes the frame to its content. The frame's own document also isolates
 * the previewed HTML's styles from the surrounding app.
 */
export const SandboxedHTMLPreview = ({ html, className, style, title, ...props }: SandboxedHTMLPreviewProps) => {
  const { t } = useTranslation("templates");
  const { height, handleLoad } = useSandboxedHTMLPreview();

  return (
    <iframe
      {...props}
      data-slot="sandboxed-html-preview"
      title={title ?? t("HTML preview")}
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={handleLoad}
      className={cn("w-full border-0", className)}
      style={{ ...style, height: height ? `${height}px` : style?.height }}
    />
  );
};
